import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { Status } from '../../../../../../../shared/builds/types';
import { LEGACY_DEP_EDIT_MODAL } from '../../../../../../../shared/featureFlags/flagNames';
import Locator from '../../../../../../../shared/Locator';
import { useIsFeatureEnabled } from '../../../../../../helpers/features/featureFlags';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import { getStatusFromDependencyState } from '../helpers';

import {
  AddAuthentication,
  CopyLocator,
  EditPackage,
  OverwritePackage,
  QueueBuild,
  RemovePackage,
  ViewBuild,
} from './ActionItems';
import type { BuildMenuProps, ErrorMenuProps, MenuProps } from '.';

export const AnalyzedMenu: VoidFunctionComponent<MenuProps> = ({
  locator,
  isManual,
  removeDependency,
  setDependencyLocatorToEdit,
  isProjectScope,
  handleOverwriteManualDependency,
}) => {
  const isUserDefinedDependency = Locator.parse(locator).fetcher === 'user';
  const shouldShowOverwrite = isProjectScope && isUserDefinedDependency;
  const isDepsDrawerV1Enabled = useIsFeatureEnabled(LEGACY_DEP_EDIT_MODAL);
  const isSetDepToEditDefined = setDependencyLocatorToEdit !== undefined;
  const shouldShowEditPackage = isDepsDrawerV1Enabled && isSetDepToEditDefined;

  return (
    <>
      {shouldShowOverwrite && (
        <OverwritePackage
          locator={locator}
          handleOverwriteManualDependency={handleOverwriteManualDependency}
        />
      )}
      <CopyLocator locator={locator} />
      {isProjectScope && (
        <RemovePackage
          locator={locator}
          isManual={isManual}
          removeDependency={removeDependency}
        />
      )}
      {shouldShowEditPackage && (
        <EditPackage locator={locator} setDependencyLocatorToEdit={setDependencyLocatorToEdit} />)}
    </>
  );
};

export const ErrorDropdown: VoidFunctionComponent<ErrorMenuProps> = ({
  locator,
  isManual,
  removeDependency,
  dependencyState,
  isProjectScope,
  handleOverwriteManualDependency,
}) => (
  <>
    {getStatusFromDependencyState(dependencyState) === Status.Unknown &&
      <AddAuthentication
        locator={locator}
      />}
    <QueueBuild
      locator={locator}
    />
    {isProjectScope && (
      <>
        <OverwritePackage
          locator={locator}
          handleOverwriteManualDependency={handleOverwriteManualDependency}
        />
        <CopyLocator
          locator={locator}
        />
        <RemovePackage
          locator={locator}
          isManual={isManual}
          removeDependency={removeDependency}
        />
      </>
    )}
  </>
);

export const BuildDropdown: VoidFunctionComponent<BuildMenuProps> = ({
  locator,
  isManual,
  removeDependency,
  setInProgressBuildId,
  isProjectScope,
  handleOverwriteManualDependency,
}) => {
  const latestBuild = useSelector(DependenciesV2Store.selectors.getLatestBuildOnRevision(locator));

  return (
    <>
      {!latestBuild ?
        <QueueBuild locator={locator} /> :
        <ViewBuild buildId={latestBuild.id} setBuildId={setInProgressBuildId} />}
      {isProjectScope && (
        <>
          <OverwritePackage
            locator={locator}
            handleOverwriteManualDependency={handleOverwriteManualDependency}
        />
          <CopyLocator
            locator={locator}
          />
          <RemovePackage
            locator={locator}
            isManual={isManual}
            removeDependency={removeDependency}
          />
        </>
      )}
    </>
  );
};
